import { MouseEvent, useState } from 'react';
import { Modal } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCopy,
  faEnvelope,
  faReply,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { Typography } from '@design-system/components/typography/typography';
import { Post } from '../model/post.types';
import { analytics } from '../lib/analytics';
import { useCommunityContext } from '../lib/communityContext';
import { useReplyInDm } from '../lib/useReplyInDm';

const MASKED_EMAIL = '************';

type Props = {
  author: Post['author'];
  open: boolean;
  onClose: () => void;
};

export const UserProfileModal = ({ author, open, onClose }: Props) => {
  const { isDmFeatureEnabled } = useCommunityContext();
  const replyInDm = useReplyInDm(author.id);
  const [copied, setCopied] = useState(false);

  const isMasked = author.marketing !== true || author.email === null;
  const showReplyInDm =
    isDmFeatureEnabled &&
    author.canReceiveDm &&
    !author.isOwn &&
    !author.isCreator;

  const close = () => {
    setCopied(false);
    onClose();
  };

  const copyEmail = () => {
    if (isMasked || author.email === null) return;
    navigator.clipboard.writeText(author.email);
    analytics.trackAuthorEmailCopied();
    setCopied(true);
  };

  const handleReplyInDm = () => {
    close();
    replyInDm();
  };

  return (
    <Modal open={open} onClose={close}>
      <div
        onClick={(event: MouseEvent<HTMLDivElement>) => event.stopPropagation()}
        className="absolute left-1/2 top-1/2 w-[326px] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-[0px_0px_1px_#EAECF0,0px_32px_64px_-12px_rgba(29,41,57,0.08)] md:w-[460px]"
        data-testid="user-profile-modal"
      >
        <div className="mb-4 flex items-center justify-between">
          <Typography
            as="h4"
            variant="body"
            size="large"
            className="text-xl font-bold"
          >
            User Profile
          </Typography>
          <button
            type="button"
            aria-label="Close"
            onClick={close}
            className="p-1 text-[#6B7280]"
            data-testid="user-profile-modal-close"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img
              src={author.avatarUrl}
              alt={author.name}
              className="size-10 shrink-0 rounded-full object-cover"
            />
            <span className="flex min-w-0 flex-col">
              <Typography as="span" variant="body" className="font-semibold">
                {author.name}
              </Typography>
              <Typography
                as="span"
                variant="body"
                size="small"
                className="text-[#6B7280]"
              >
                #{author.id}
              </Typography>
            </span>
          </div>
          {showReplyInDm && (
            <button
              type="button"
              onClick={handleReplyInDm}
              className="flex items-center gap-1.5 text-sm font-medium text-[#5F1ECC]"
              data-testid="user-profile-modal-reply-dm"
            >
              <FontAwesomeIcon icon={faReply} />
              Reply via DM
            </button>
          )}
        </div>

        <div className="grid grid-cols-[1fr_auto] items-center gap-y-2 border-t border-[#ECEDF2] pt-4">
          <span className="flex items-center gap-2">
            <FontAwesomeIcon icon={faEnvelope} className="text-[#131313]" />
            <Typography
              as="span"
              variant="body"
              size="small"
              data-testid="user-profile-modal-email"
            >
              {isMasked ? MASKED_EMAIL : author.email}
            </Typography>
          </span>
          <button
            type="button"
            onClick={copyEmail}
            disabled={isMasked}
            className={
              copied
                ? 'flex items-center gap-1.5 text-sm font-medium text-[#16A34A]'
                : 'flex items-center gap-1.5 text-sm font-medium text-[#5F1ECC] disabled:text-[#9CA3AF]'
            }
            data-testid="user-profile-modal-copy"
          >
            <FontAwesomeIcon icon={copied ? faCircleCheck : faCopy} />
            {copied ? 'Copied' : 'Copy'}
          </button>
          {author.marketing !== null && (
            <Typography
              as="span"
              variant="body"
              size="small"
              className="col-start-2 text-right"
            >
              Marketing:{' '}
              <span
                className={
                  author.marketing ? 'text-[#16A34A]' : 'text-[#E5322D]'
                }
              >
                {author.marketing ? 'Yes' : 'No'}
              </span>
            </Typography>
          )}
        </div>
      </div>
    </Modal>
  );
};
